import React, { FC, useMemo, useState } from "react";
import {
    Box,
    Flex,
    Text,
    Anchor,
    Modal,
    TextInput,
    ScrollArea,
    Stack,
    Group,
    CopyButton,
    Tooltip,
    ActionIcon,
    Button
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconBulb, IconCheck, IconCopy } from "@tabler/icons-react";
import { ReplacementTagBadge } from "./ReplacementTagBadge";
import classes from "./ReplacementTagInfo.module.css";

export interface ReplacementTag {
    Group: string;
    Name: string;
    Description: string;
    Module: null | number;
}

interface ReplacementTagInfoProps {
    replacementTags: ReplacementTag[];
}

export const ReplacementTagInfo: FC<ReplacementTagInfoProps> = ({ replacementTags }) => {
    const [opened, { open, close }] = useDisclosure(false);
    const [search, setSearch] = useState('');

    const filteredTags = useMemo(() => {
        if (!search) return replacementTags;
        const s = search.toLowerCase();
        return replacementTags.filter(t =>
            (t.Name?.toLowerCase() || '').includes(s) ||
            (t.Description?.toLowerCase() || '').includes(s)
        );
    }, [replacementTags, search]);

    return (
        <>
            <Box ml={{ base: 0, lg: 'auto' }} w={{ base: '100%', lg: 'auto' }}>
                <Flex
                    bg="scBlue.0"
                    p="xs"
                    px="sm"
                    style={{
                        borderRadius: 8,
                        border: '1px solid var(--mantine-color-scBlue-1)',
                    }}
                    gap="sm"
                    align="flex-start"
                >
                    <IconBulb size={20} color="var(--mantine-color-scBlue-6)" style={{ marginTop: 2 }} />
                    <Box>
                        <Text size="sm" fw={700} c="scBlue.7" lh={1.2}>Hint</Text>
                        <Text size="sm" c="dark.7" lh={1.4}>
                            Type @ anywhere in the subject or body to open the tag picker. <Anchor size="sm" fw={700} component="button" type={'button'} onClick={open}>See all tags</Anchor>
                        </Text>
                    </Box>
                </Flex>
            </Box>

            <Modal
                withCloseButton
                opened={opened}
                onClose={close}
                title={<Text fw={700} size="lg">Replacement tags</Text>}
                size="md"
                radius={6}
                padding="xl"
            >
                <TextInput
                    placeholder="Search replacement tag"
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    mb="xl"
                    size="md"
                    radius="md"
                    styles={{
                        input: {
                            backgroundColor: 'var(--mantine-color-gray-0)',
                            border: 'none'
                        }
                    }}
                />

                <ScrollArea.Autosize h={'min(400px, calc(100vh - 300px))'} offsetScrollbars>
                    <Stack gap="sm">
                        {filteredTags.map((tag) => (
                            <Group key={tag.Name} justify="space-between" wrap="nowrap" className={classes.tagRow}>
                                <Text size="sm" c="dark.6">{tag.Description || tag.Name}</Text>
                                <Group gap="xs" pr={'xs'} wrap="nowrap">
                                    <ReplacementTagBadge>
                                        {tag.Name}
                                    </ReplacementTagBadge>
                                    <CopyButton value={`{{${tag.Name}}}`} timeout={2000}>
                                        {({ copied, copy }) => (
                                            <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="top" color={'scBlue'}>
                                                <ActionIcon
                                                    color={copied ? 'scBlue' : 'gray.5'}
                                                    variant="subtle"
                                                    onClick={copy}
                                                    size="sm"
                                                    className={`${classes.copyButton} ${copied ? classes.copied : ''}`}
                                                >
                                                    {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </CopyButton>
                                </Group>
                            </Group>
                        ))}
                    </Stack>
                </ScrollArea.Autosize>

                <Flex justify="flex-end" mt="xl">
                    <Button onClick={close} variant={'default'} type={'button'} size="sm" px="xl" >
                        Close
                    </Button>
                </Flex>
            </Modal>
        </>
    );
};
